import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams } from 'react-router-dom';
import { Box, CircularProgress, CssBaseline, ThemeProvider, Typography } from '@mui/material';
import './App.css';
import { AuthProvider, useAuth } from './AuthProvider';
import { theme } from './styles/theme';
import { LocationProvider } from './contexts/LocationContext';

// Import components
import Home from './components/Home';
import ViewPub from './components/ViewPub';
import FindPubs, { PubObject } from './components/FindPubs';
import SearchArea from './components/SearchArea';
import About from './components/About';
import NotFound from './components/NotFound';
import WelcomePage from './components/WelcomePage';
import { Background } from "./styles/Background";
import ScrollIndicator from './components/ScrollIndicator';
import CityLandingPage from './components/CityLandingPage';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import InstallPWA from './components/InstallPWA';
import ModerationPanel from './components/ModerationPanel';
export { ScrollIndicator };

const ViewPubWrapper = () => {
  const location = useLocation();
  const pubFromLocation = location.state?.pubToView || null;
  const { googlePlaceId } = useParams<{ googlePlaceId: string }>();
  
  if (pubFromLocation) {
    localStorage.setItem('currentViewPubGPId', pubFromLocation.uid);
    return <ViewPub pubToView={pubFromLocation} />;
  }
  
  if (googlePlaceId) {
    return <ViewPub googlePlaceId={googlePlaceId} />;
  }
  
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh' 
    }}>
      <CircularProgress />
    </Box>
  );
};

const AppCore: React.FC = () => {
  const [title, setTitle] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [pubToView, setPubToView] = useState<PubObject | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showBurgerMenuIcon, setShowBurgerMenuIcon] = useState<boolean>(true);
  const { loading: authLoading } = useAuth();

  const getStoredCoordinates = () => {
    const storedLat = localStorage.getItem('userLatitude');
    const storedLon = localStorage.getItem('userLongitude');
    const hasPermission = localStorage.getItem('locationPermissionGranted') === 'true';

    if (hasPermission && storedLat && storedLon) {
      return {
        latitude: parseFloat(storedLat),
        longitude: parseFloat(storedLon)
      };
    }

    return {
      latitude: 51.4700777,
      longitude: -0.0245202
    };
  };

  const initialCoords = getStoredCoordinates();
  const [latitude, setLatitude] = useState<number>(initialCoords.latitude);
  const [longitude, setLongitude] = useState<number>(initialCoords.longitude);

  useEffect(() => {
    import('./API').then(({ storeUserTimezone }) => {
      storeUserTimezone();
    });
  }, []);

  useEffect(() => {
    // This effect now only runs when auth is ready, because the component itself
    // is blocked from rendering until authLoading is false.
    const currentViewPubGPId = localStorage.getItem('currentViewPubGPId');
    
    if (currentViewPubGPId && currentViewPubGPId !== "undefined" && !pubToView) {
      import('./API').then(({ getPubByGooglePlaceId: getPubById }) => {
        getPubById(currentViewPubGPId)
          .then(response => {
            let foundPub = null;
            if (response.data && response.data.pub) {
                foundPub = response.data.pub;
            } else if (response.data && response.data.pubs && Array.isArray(response.data.pubs) && response.data.pubs.length > 0) {
                foundPub = response.data.pubs[0];
            } else if (response.data && response.data.google_place_id) {
                foundPub = response.data;
            }

            if (foundPub) {
              setPubToView(foundPub as PubObject);
            }
          })
          .catch(error => {
            console.error("Error restoring pub data:", error);
            localStorage.removeItem('currentViewPubGPId');
          });
      });
    } else if (currentViewPubGPId === "undefined") {
        console.warn("App.tsx: Found 'undefined' string in localStorage key 'currentViewPubGPId'. Removing it.");
        localStorage.removeItem('currentViewPubGPId');
    }
  }, [pubToView]); // authLoading is no longer needed as a dependency.

  // Don't render the main application routes until authentication is initialized.
  if (authLoading) {
    return (
      <Background>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          width: '100%',
        }}>
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
      </Background>
    );
  }

  // Auth is ready, render the full application.
  return (
    <Background>
      <Router>
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            width: '100%',
          }}
        >
          <Routes>
            <Route path="/" element={
              <Home 
                setTitle={setTitle}
                search={search}
                setSearch={setSearch}
                setLatitude={setLatitude}
                setLongitude={setLongitude}
                setShowBurgerMenuIcon={setShowBurgerMenuIcon}
                loading={loading}
                setLoading={setLoading}
              />
            } />
            <Route path="/search-area" element={
              <SearchArea
                setTitle={setTitle}
                setShowBurgerMenuIcon={setShowBurgerMenuIcon}
                setLoading={setLoading}
              />
            } />
            <Route path="/view_pub" element={<ViewPubWrapper />} />
            <Route path="/pub/:googlePlaceId" element={<ViewPubWrapper />} />
            <Route path="/pubs" element={
              <FindPubs
                search={search}
                setSearch={setSearch}
                setShowBurgerMenuIcon={setShowBurgerMenuIcon}
                loading={loading}
                setLoading={setLoading}
              />
            } />
            <Route path="/find-pubs" element={
              <FindPubs
                search={search}
                setSearch={setSearch}
                setShowBurgerMenuIcon={setShowBurgerMenuIcon}
                loading={loading}
                setLoading={setLoading}
              />
            } />
            <Route path="/about" element={
              <About
                setTitle={setTitle}
                setShowBurgerMenuIcon={setShowBurgerMenuIcon}
              />
            } />
            <Route path="/sunny-pubs-in/:encodedCity" element={<CityLandingPage />} />
            <Route path="/terms-of-use" element={
              <TermsOfUse
                setTitle={setTitle}
                setShowBurgerMenuIcon={setShowBurgerMenuIcon}
              />
            } />
            <Route path="/privacy-policy" element={
              <PrivacyPolicy
                setTitle={setTitle}
                setShowBurgerMenuIcon={setShowBurgerMenuIcon}
              />
            } />
            <Route path="/moderate" element={<ModerationPanel />} />
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <InstallPWA />
        </Box>
      </Router>
    </Background>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <LocationProvider>
          <AppCore />
        </LocationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;